import { useMemo, useState } from "react";
import { computeSummary } from "@/lib/calc";
import { CompensationInput } from "@/components/CompensationInput";
import { ScenarioSummary, KeyMetricsGrid } from "@/components/ScenarioSummary";
import { ComparisonChart } from "@/components/ComparisonChart";
import { MonthlyTracker } from "@/components/MonthlyTracker";
import { SavedTrackers } from "@/components/SavedTrackers";
import { Button } from "@/components/ui/button";
import { Printer, LineChart } from "lucide-react";

export default function Calculator() {
  const [mode, setMode] = useState("monthly");
  const [monthly, setMonthly] = useState(180000);
  const [historicalRate, setHistoricalRate] = useState(0.70);
  const [actuals, setActuals] = useState(new Array(12).fill(0));

  const summary = useMemo(
    () => computeSummary(monthly, historicalRate),
    [monthly, historicalRate]
  );

  const handlePrint = () => {
    window.print();
  };

  return (
    <main className="min-h-screen bg-slate-50 grid-bg">
      {/* Header */}
      <header
        className="sticky top-0 z-30 border-b border-slate-200 bg-white/85 backdrop-blur-xl no-print"
        data-testid="app-header"
      >
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-blue-900 text-white flex items-center justify-center">
              <LineChart className="w-5 h-5" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-slate-500">
                SETU
              </p>
              <h1 className="font-display text-lg text-slate-900 leading-tight">
                Incentive Policy Calculator
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handlePrint}
              variant="outline"
              className="border-slate-300 text-slate-700 hover:bg-slate-900 hover:text-white transition-colors duration-200"
              data-testid="btn-print-pdf"
            >
              <Printer className="w-4 h-4 mr-2" />
              Export PDF / Print
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-6 py-8">
        {/* Hero */}
        <section className="mb-10 no-print">
          <p className="text-[11px] uppercase tracking-[0.28em] font-semibold text-blue-800">
            Compensation &amp; Revenue Simulator
          </p>
          <h2 className="font-display text-4xl sm:text-5xl mt-3 max-w-3xl text-slate-900 font-light tracking-tight">
            Model incentives across{" "}
            <span className="text-blue-800 font-medium">Current</span> and{" "}
            <span className="text-emerald-700 font-medium">Counter-Offer</span>{" "}
            structures — with live 12-month tracking.
          </h2>
          <p className="text-base text-slate-600 mt-4 max-w-2xl">
            Enter monthly or annual compensation. All benchmarks — 10× break-even, 11×
            counter-offer and 15× stretch — are computed instantly.
          </p>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          <div className="space-y-6">
            <CompensationInput
              mode={mode}
              setMode={setMode}
              monthly={monthly}
              setMonthly={setMonthly}
              historicalRate={historicalRate}
              setHistoricalRate={setHistoricalRate}
            />

            <KeyMetricsGrid summary={summary} />

            <ScenarioSummary summary={summary} />

            <ComparisonChart summary={summary} />

            <MonthlyTracker
              summary={summary}
              actuals={actuals}
              setActuals={setActuals}
            />
          </div>

          <SavedTrackers
            monthly={monthly}
            historicalRate={historicalRate}
            actuals={actuals}
            setMonthly={setMonthly}
            setHistoricalRate={setHistoricalRate}
            setActuals={setActuals}
          />
        </div>

        <footer className="mt-16 pt-6 border-t border-slate-200 text-xs text-slate-500 flex items-center justify-between no-print">
          <span>SETU Incentive Policy · Built to match FY-27 policy workbook.</span>
          <span className="font-mono-num">v1.0</span>
        </footer>
      </div>
    </main>
  );
}
